import type { RequestInfo } from '@ember-data/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import type { TasAdminActor } from '../utils/tas-actor.ts';

const tasProgramTemplatesUrl = (path?: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'tas-program-templates' },
    op: 'query',
    resourcePath: 'tas-program-templates',
  };
  const base = buildBaseURL(opts);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
};

function transitionRequest(
  templateId: string,
  event: 'launch' | 'suspend',
  by: TasAdminActor,
  note: string
): RequestInfo {
  // Top-level body shape, matching what the legacy admin adapter sent
  // via `this.ajax(url, 'PATCH', { data: { type, id, note, by } })` —
  // Ember Data's `ajax` puts `options.data` directly into the body, so
  // these four fields land at the top level of the request body, NOT
  // inside a JSON:API `data` envelope. The transition-state endpoint
  // reads them flat.
  return {
    url: tasProgramTemplatesUrl(`${templateId}/transition/state/${event}`),
    method: 'PATCH',
    body: JSON.stringify({
      type: 'tas-program-templates',
      id: templateId,
      note,
      by,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** PATCH /tas-program-templates/:id/transition/state/launch — admin only. */
export function launchTasProgramTemplateRequest(
  templateId: string,
  by: TasAdminActor,
  note = ''
): RequestInfo {
  return transitionRequest(templateId, 'launch', by, note);
}

/** PATCH /tas-program-templates/:id/transition/state/suspend — admin only. */
export function suspendTasProgramTemplateRequest(
  templateId: string,
  by: TasAdminActor,
  note = ''
): RequestInfo {
  return transitionRequest(templateId, 'suspend', by, note);
}

/**
 * POST /tas-program-templates/:id/apply-for-assistance — employee flow
 * that initializes either a TasApplication or TasProgramInstance for
 * the given employee (+ optional dependent). The legacy adapter sat on
 * `response.data`; that lives at `(await store.request(...)).content.data`
 * here.
 */
export interface InitializeTypedProgramPayload {
  templateId: string;
  employeeId: string;
  eligibilityDate: string;
  dependentId?: string;
}

export function initializeTypedProgramRequest(payload: InitializeTypedProgramPayload): RequestInfo {
  return {
    url: tasProgramTemplatesUrl(`${payload.templateId}/apply-for-assistance`),
    method: 'POST',
    body: JSON.stringify({
      employee_id: payload.employeeId,
      eligibility_date: payload.eligibilityDate,
      dependent_id: payload.dependentId,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}
